import { useCollection } from '@amzn/awsui-collection-hooks';
import { Box, Button, Header, Pagination, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { logger } from 'src/logger';
import { DEFAULT_FILE_NAME, DEFAULT_SHEET_NAME, formatTableDataForExport } from 'src/utilities/CloudscapeTableUtils';
import { exportAsExcelFile } from 'src/utilities/FileServices';
import { EmptyStateMessage } from '../EmptyStateMessage';
import zebraStyles from './FinOpsTable.module.scss';

// Constants
const DEFAULT_PAGE_SIZE = 3;
/**
 * Page size used when `disablePagination` is set: a large-but-finite sentinel that exceeds
 * any realistic table row count, so `useCollection` yields a single page. Bounded (not
 * `Number.MAX_SAFE_INTEGER`) to avoid depending on the collection hook's arithmetic on an
 * extreme value.
 */
const ALL_ROWS_PAGE_SIZE = 10_000;

// Base interface for our table component - contains all the common props
// that we need regardless of whether download is enabled or not
interface BaseDetailsTableProps {
  emptyMessage: string;
  filterProperties: any;
  visibleContent?: readonly string[];
  data: readonly Record<string, any>[];
  columnDefinitions: readonly TableProps.ColumnDefinition<any>[];
  pageSize?: number;
  /**
   * Render every row on a single page with no pagination control. Opt-in and off by
   * default, so existing callers are unaffected. When set, the whole (sortable) data set
   * is shown at once — use only for tables whose row count is intentionally bounded
   * (e.g. §4b after its Pareto "Others" collapse).
   */
  disablePagination?: boolean;
  headerName?: string;
  /**
   * Custom React node rendered in the table header's left slot. Takes precedence
   * over `headerName` when both are provided. Use this to render a styled title
   * (e.g. the OE report's blue `subLabel`) inline with pagination.
   */
  headerContent?: React.ReactNode;
  stickyHeader?: boolean;
  /**
   * Pins the given number of leading/trailing columns in place while the table scrolls
   * horizontally, so a wide table keeps its identifying column (e.g. Application) in
   * view. Forwarded straight to Cloudscape's `Table`, which deactivates the feature on
   * its own when there is too little scrollable width for it to make sense. Omit to
   * leave all columns scrolling (the default), so existing callers are unaffected.
   */
  stickyColumns?: TableProps<any>['stickyColumns'];
  /**
   * Enables Cloudscape inline cell editing. Only takes effect for columns that also
   * declare `editConfig`. Omit to keep the table read-only (the default).
   */
  submitEdit?: TableProps<any>['submitEdit'];
  /**
   * Tints every other body row a light color to make rows easier to track across
   * wide tables. Off by default so existing callers of this shared component are
   * unaffected. Deliberately distinct from Cloudscape's own `stripedRows`, which
   * always uses the framework's neutral gray — this is a specific color a caller
   * can set via `zebraColor`, not tied to row status/severity.
   */
  zebraStripe?: boolean;
  /** Overrides the default light-blue tint used when `zebraStripe` is set. Any valid CSS color. */
  zebraColor?: string;
  /**
   * Predicate identifying rows that must always render at the very bottom, unaffected
   * by the user's column sort (e.g. a "Grand Total" summary row). Matching rows are
   * held out of sorting/filtering/pagination and appended after the sorted items, so
   * they stay pinned in both ascending and descending order — Cloudscape's own
   * `sortingComparator` cannot express this because it is direction-agnostic. Off by
   * default, so existing callers of this shared component are unaffected.
   */
  pinLastRows?: (row: Record<string, any>) => boolean;
  /**
   * Wraps long cell text onto multiple lines instead of truncating. Off by default so
   * existing callers are unaffected; set true for tables with long text columns
   * (e.g. root cause / summary) so they read like the Full Tracker.
   */
  wrapLines?: boolean;
}

// Props interface for when we don't want download functionality
// Note: downloadFileName is explicitly blocked here to prevent confusion
interface WithoutDownloadProps extends BaseDetailsTableProps {
  enableDownloadReport?: false;
  downloadFileName?: never; // Blocks downloadFileName when downloads are disabled
}

// Props interface for when we want download functionality enabled
// Forces downloadFileName to be provided since it's needed for the export
interface WithDownloadProps extends BaseDetailsTableProps {
  enableDownloadReport: true;
  downloadFileName: string; // Required for file export naming
}

// Union type that gives us compile-time checking of required props
// based on whether downloads are enabled or not
type DetailsTableProps = WithoutDownloadProps | WithDownloadProps;

/**
 * FinOpsTable Component
 * Displays data in a table format with filtering, pagination, and export capabilities
 */
export const FinOpsTable: React.FC<DetailsTableProps> = (props) => {
  const { enableDownloadReport = false } = props;
  logger.info('Rendering DetailsTableWithProps with data length:' + props.data.length);

  // Input validation
  if (!Array.isArray(props.data)) {
    logger.error('Invalid data prop provided:', props.data);
    throw new Error('Data must be an array');
  }

  // Dev-only: warn once when the two mutually-exclusive props are both set, rather than on
  // every render (a frequently-re-rendering table would otherwise spam the console).
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && props.disablePagination && props.pageSize !== undefined) {
      logger.warn('FinOpsTable: `pageSize` is ignored when `disablePagination` is set.');
    }
  }, [props.disablePagination, props.pageSize]);

  // Derived (not frozen in state): when `disablePagination` is set, every row renders on one
  // page; otherwise the caller's `pageSize` (or the default) applies. Deriving means a parent
  // that toggles `disablePagination` after mount is honored, keeping the page size in step
  // with the shown/hidden Pagination control.
  const effectivePageSize = props.disablePagination ? ALL_ROWS_PAGE_SIZE : props.pageSize || DEFAULT_PAGE_SIZE;

  // Rows the caller wants pinned to the bottom are held out of the collection so the
  // user's sort/filter/pagination only ever reorders the rest; they are re-appended
  // after `items` below. Split up front so `useCollection` never sees them.
  const { pinnedRows, sortableRows } = useMemo(() => {
    if (!props.pinLastRows) return { pinnedRows: [] as readonly Record<string, any>[], sortableRows: props.data };
    const pinned: Record<string, any>[] = [];
    const sortable: Record<string, any>[] = [];
    props.data.forEach((row) => (props.pinLastRows!(row) ? pinned : sortable).push(row));
    return { pinnedRows: pinned, sortableRows: sortable };
  }, [props.data, props.pinLastRows]);

  // Collection hook setup
  const { items, actions, collectionProps, paginationProps } = useCollection(sortableRows, {
    propertyFiltering: {
      filteringProperties: props.filterProperties,
      empty: <EmptyStateMessage title="No data" subtitle="No data to display." />,
      noMatch: (
        <EmptyStateMessage
          title="No matches"
          subtitle="No match found to display."
          action={<Button onClick={() => actions.setFiltering('')}>Clear Filter</Button>}
        />
      )
    },
    pagination: { pageSize: effectivePageSize },
    sorting: {},
    selection: {}
  });

  // Whether the current page is the last one, so pinned rows append only there rather
  // than repeating on every page. `pagesCount` is undefined until the collection has
  // paginated; treat that (and a single page) as "last page".
  const isLastPage = !paginationProps.pagesCount || (paginationProps.currentPageIndex ?? 1) >= paginationProps.pagesCount;

  // Append pinned rows only when there are sortable results on the last page. Skipping
  // the append when `items` is empty (e.g. a filter matched nothing) lets Cloudscape's
  // "No matches" empty state render instead of a lone, contextless Grand Total row.
  const showPinnedRows = pinnedRows.length > 0 && isLastPage && items.length > 0;

  /**
   * Handles the export functionality
   */
  const onDownloadClick = useCallback(() => {
    logger.info('Starting download process');
    try {
      const exportData = formatTableDataForExport(props.data, props.columnDefinitions);
      const fileName = props.downloadFileName ?? DEFAULT_FILE_NAME;
      exportAsExcelFile(exportData, fileName, DEFAULT_SHEET_NAME);
      logger.info('Download completed successfully');
    } catch (error: any) {
      logger.error('Error during download:', error);
    }
  }, [props.data, props.columnDefinitions, props.downloadFileName]);

  /**
   * Renders the table header with pagination and download button
   */
  const tableHeader = (
    <Box>
      {/* Count the real data rows, excluding any pinned summary rows (e.g. a Grand
          Total) so the header count is not inflated. Falls back to the full length
          when pinning is not used, leaving existing callers unchanged. */}
      <Box float="left">
        {props.headerContent
          ? props.headerContent
          : props.headerName && <Header counter={`(${props.pinLastRows ? sortableRows.length : props.data.length})`}>{props.headerName}</Header>}
      </Box>
      <Box float="right">
        <SpaceBetween size="m" direction="horizontal">
          {!props.disablePagination && <Pagination {...paginationProps} />}
          {enableDownloadReport && (
            <Button iconName="download" onClick={onDownloadClick} disabled={!props.data.length}>
              Download
            </Button>
          )}
        </SpaceBetween>
      </Box>
    </Box>
  );

  // Performance optimization for table props
  const tableProps = {
    variant: 'embedded' as const,
    ...collectionProps,
    columnDefinitions: props.columnDefinitions,
    // Pinned rows trail the sorted items so a "Grand Total" stays at the bottom
    // regardless of sort direction. Only on the final page with results (see
    // `showPinnedRows`). No-op when `pinLastRows` is not supplied.
    items: showPinnedRows ? [...items, ...pinnedRows] : items,
    loadingText: 'Loading details',
    visibleColumns: props.visibleContent,
    resizableColumns: true,
    wrapLines: props.wrapLines ?? false,
    stickyHeader: props.stickyHeader ?? true,
    empty: <EmptyStateMessage title="No data" subtitle={props.emptyMessage} />,
    header: tableHeader,
    // Only forwarded when supplied, so tables that don't opt in keep every column scrolling.
    ...(props.stickyColumns ? { stickyColumns: props.stickyColumns } : {}),
    // Only forwarded when supplied, so read-only callers are unaffected.
    ...(props.submitEdit ? { submitEdit: props.submitEdit } : {})
  };

  const table = <Table {...tableProps} />;
  if (!props.zebraStripe) return table;

  // Wrapping rather than modifying `Table` itself: `zebraStripe`'s CSS targets plain
  // `<tr>` elements (see `FinOpsTable.module.scss`), so it only needs to scope itself
  // to this instance's DOM subtree — no prop exists on `Table` for per-row background.
  return (
    <div
      className={zebraStyles.zebraStripe}
      style={props.zebraColor ? ({ '--fin-ops-table-zebra-color': props.zebraColor } as React.CSSProperties) : undefined}
    >
      {table}
    </div>
  );
};
